import React, { useState } from 'react';
import Modal from './Modal';
import { useToast } from '../../context/ToastContext';
import { Upload, Download, FileSpreadsheet } from 'lucide-react';
import { parseCSV, rowsToPayloads } from '../../utils/importExcel';
import { downloadCSV } from '../../utils/exportExcel';

// Reusable CSV importer for master screens.
// Props:
//   open, onClose, title
//   columns:    [{ key, label, type:'text'|'number'|'bool', required }]
//   sample:     optional example object (keyed by column.key) for the template
//   existing:   array of existing values to dedupe against (compared case-insensitively)
//   dedupeKey:  payload field used for dedupe (e.g. 'name')
//   onAdd:      (payload) => void   — adds one record (dispatch ADD_*)
export default function ImportModal({ open, onClose, title, columns, sample, existing = [], dedupeKey, onAdd }) {
  const { addToast } = useToast();
  const [rows, setRows] = useState(null);     // [{rowNo, payload, missing}]
  const [fileName, setFileName] = useState('');
  const [done, setDone] = useState(null);

  const existingSet = new Set((existing || []).map(v => String(v || '').toLowerCase().trim()));

  const downloadTemplate = () => {
    const headers = columns.map(c => c.label + (c.required ? ' *' : ''));
    const sampleRow = columns.map(c => (sample && sample[c.key] != null ? String(sample[c.key]) : ''));
    downloadCSV(`${title.replace(/\s+/g, '-').toLowerCase()}-template`, headers, [sampleRow]);
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setFileName(file.name);
    setDone(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = parseCSV(reader.result);
        setRows(rowsToPayloads(parsed, columns));
      } catch (err) {
        addToast('Could not read the file: ' + err.message, 'error');
      }
    };
    reader.readAsText(file);
  };

  const analysis = () => {
    if (!rows) return null;
    let dup = 0, bad = 0, ok = 0;
    rows.forEach(r => {
      if (r.missing.length) { bad++; return; }
      if (dedupeKey && existingSet.has(String(r.payload[dedupeKey] || '').toLowerCase().trim())) { dup++; return; }
      ok++;
    });
    return { dup, bad, ok, total: rows.length };
  };

  const handleImport = () => {
    if (!rows) return;
    const seen = new Set(existingSet);
    let added = 0, skipped = 0;
    rows.forEach(r => {
      if (r.missing.length) { skipped++; return; }
      const key = dedupeKey ? String(r.payload[dedupeKey] || '').toLowerCase().trim() : null;
      if (key != null && seen.has(key)) { skipped++; return; }
      try { onAdd(r.payload); added++; if (key != null) seen.add(key); }
      catch { skipped++; }
    });
    setDone({ added, skipped });
    addToast(`Imported ${added} record(s)${skipped ? `, skipped ${skipped}` : ''}`, added ? 'success' : 'info');
  };

  const close = () => { setRows(null); setFileName(''); setDone(null); onClose(); };
  const a = analysis();

  return (
    <Modal isOpen={open} onClose={close} title={title} size="lg">
      <div className="space-y-4">
        <div className="text-sm text-gray-600">
          Import records from a CSV file. Download the template, fill it in (keep the header row),
          then upload. Required columns are marked with <span className="font-mono">*</span>.
          {dedupeKey && <> Existing records (matched on <b>{columns.find(c => c.key === dedupeKey)?.label}</b>) are skipped.</>}
        </div>

        <div className="flex flex-wrap gap-3">
          <button onClick={downloadTemplate} className="btn btn-secondary btn-sm flex items-center gap-2">
            <Download size={15} /> Download Template
          </button>
          <input type="file" accept=".csv,text/csv" id="import-file" className="hidden" onChange={handleFile} />
          <label htmlFor="import-file" className="btn btn-secondary btn-sm cursor-pointer flex items-center gap-2">
            <Upload size={15} /> Choose CSV{fileName ? ` — ${fileName}` : ''}
          </label>
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600">
          <div className="font-semibold text-gray-700 mb-1 flex items-center gap-1"><FileSpreadsheet size={13} /> Columns</div>
          {columns.map(c => (
            <span key={c.key} className="inline-block mr-3">{c.label}{c.required ? ' *' : ''}{c.type === 'number' ? ' (number)' : c.type === 'bool' ? ' (yes/no)' : ''}</span>
          ))}
        </div>

        {a && !done && (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
            <b>{a.total}</b> row(s) read — <b>{a.ok}</b> ready to import
            {a.dup > 0 && <>, <b>{a.dup}</b> duplicate(s) skipped</>}
            {a.bad > 0 && <>, <b>{a.bad}</b> with missing required fields</>}.
          </div>
        )}

        {done && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
            ✓ Imported <b>{done.added}</b> record(s){done.skipped ? <>, skipped <b>{done.skipped}</b></> : ''}.
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
        <button onClick={close} className="btn btn-secondary">{done ? 'Close' : 'Cancel'}</button>
        {!done && (
          <button onClick={handleImport} disabled={!a || a.ok === 0} className="btn btn-primary flex items-center gap-2 disabled:opacity-50">
            <Upload size={16} /> Import{a ? ` ${a.ok}` : ''}
          </button>
        )}
      </div>
    </Modal>
  );
}
